/* eslint-disable react-hooks/exhaustive-deps */
import { forwardRef, useEffect, useRef, useState } from 'react';

import Tippy from '@tippyjs/react';

import { cn } from 'lib/helpers/utility';
import { TOOLTIP_TOUCH_HOLD } from 'lib/constants/misc';

import Icon from 'components/shared/Icon/Icon';
import Loader from 'components/shared/Loader/Loader';

import ButtonGroup from './components/ButtonGroup';
import {
  baseButtonClasses,
  buttonIconSizes,
  buttonSizes,
  generateButtonVariants,
  iconColorsTypes,
  textButtonClasses,
  widthButtonSizes
} from './lib/variants';

/**
 * @type {import('react').ForwardRefExoticComponent<import('./lib/propTypes').ButtonProps & import('react').RefAttributes<HTMLElement>>}
 */

const Button = forwardRef((props, ref) => {
  const {
    variant = 'primary',
    style = 'default',
    size = 'medium',
    className = '',
    text,
    iconColor,
    color,
    shade = 500,
    icon,
    iconRight,
    tooltip,
    tooltipPlacement = 'bottom',
    tooltipColor = 'dark',
    iconIsStroke = false,
    iconSize,
    iconShade,
    isDisabled = false,
    iconClassName = '',
    textClassName = '',
    isLoading = false,
    isFirst = false,
    isLast = false,
    isGroup = false,
    hideTextOnSmallDevice = false,
    wrapperClassName = '',
    buttonType = 'button',
    as: Component = 'button',
    ...rest
  } = props;

  const buttonVariantStyle = generateButtonVariants(color, shade)[variant][style];

  const buttonClass = cn(
    baseButtonClasses,
    buttonSizes[size],
    textButtonClasses,
    buttonVariantStyle,
    (isDisabled || isLoading) && 'cursor-not-allowed',
    isDisabled && !isLoading && 'opacity-50',
    isGroup && 'rounded-none',
    isFirst && 'rounded-l-md',
    isLast && 'rounded-r-md',
    !text && widthButtonSizes[size],
    className
  );

  const currentIconColor = iconColor || iconColorsTypes(color, style)[variant];
  // A disabled button does nothing on tap, so its tooltip (usually the reason) still opens on one.
  const tooltipTouch = isDisabled || TOOLTIP_TOUCH_HOLD;

  const renderIcon = (iconData) =>
    iconData && (
      <span className="flex items-center">
        <Icon
          icon={iconData}
          color={currentIconColor}
          stroke={iconIsStroke}
          shade={iconShade}
          size={iconSize || buttonIconSizes[size]}
          className={iconClassName}
        />
      </span>
    );

  const renderLoader = () => (
    <Loader color={iconColor} shade={iconShade} size={iconSize || buttonIconSizes[size]} />
  );

  return (
    <Tippy
      content={tooltip || ''}
      disabled={!tooltip}
      placement={tooltipPlacement}
      touch={tooltipTouch}
      className={`tippy-${tooltipColor}`}>
      <div className={wrapperClassName}>
        <Component
          ref={ref}
          data-qa={text}
          className={buttonClass}
          {...(Component === 'button'
            ? { disabled: isDisabled || isLoading, type: buttonType }
            : { 'aria-disabled': isDisabled || isLoading })}
          {...rest}>
          {isLoading ? renderLoader() : renderIcon(icon)}
          {text && (
            <span
              className={cn(
                'whitespace-nowrap lowercase first-letter:uppercase',
                hideTextOnSmallDevice && 'md:hidden sm:hidden',
                textClassName
              )}>
              {text}
            </span>
          )}
          {renderIcon(iconRight)}
        </Component>
      </div>
    </Tippy>
  );
});

const Dropdown = ({
  items = [],
  dropdownClassName = '',
  active,
  onClick = () => {},
  open,
  setOpen,
  ...rest
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = open !== undefined && typeof setOpen === 'function';

  const isOpen = isControlled ? open : internalOpen;
  const toggleOpen = () => {
    isControlled ? setOpen(!open) : setInternalOpen((prev) => !prev);
  };
  const close = () => {
    isControlled ? setOpen(false) : setInternalOpen(false);
  };

  const dropdownRef = useRef();
  const left = dropdownRef.current?.getBoundingClientRect().left;

  const handleClickOutside = (e) => {
    if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
      close();
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative inline-block" ref={dropdownRef}>
      <Button
        {...rest}
        onClick={toggleOpen}
        className={cn(rest.className, isOpen && 'bg-neutral-100')}
      />
      {isOpen && (
        <div
          className={cn(
            'absolute z-50 mt-2 flex w-fit flex-col gap-1 rounded-[4px] bg-white py-2 shadow-xs',
            left > 200 ? `right-0` : 'left-0',
            dropdownClassName
          )}>
          {items.map((item, index) => (
            <button
              key={index}
              onClick={() => {
                item.onClick?.();
                onClick(item);
                close();
              }}
              className={cn(
                'flex h-8 w-full cursor-pointer items-center gap-2 whitespace-nowrap px-4 text-left hover:bg-primary-25',
                active === item.value && 'bg-primary-25'
              )}>
              {item.icon && <Icon icon={item.icon} color={item.iconColor || 'neutral'} size={16} />}
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

Button.Group = ButtonGroup;
Button.Dropdown = Dropdown;
Button.displayName = 'Button';

export default Button;
